"use client";

import * as React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "./ui/button";
import { Card } from "./ui/card";
import { Progress } from "./ui/progress";
import { Badge } from "./ui/badge";
import { AstroMascot } from "./AstroMascot";
import { 
  ArrowLeft, 
  Upload, 
  FileText,
  FileImage,
  GraduationCap,
  Briefcase,
  User,
  Check,
  X,
  RefreshCw,
  Sparkles,
  Rocket,
  Files
} from "lucide-react";
const openTurfLogo = '/openturf-logo.svg';

interface Document {
  id: string;
  file: File;
  type: DocumentType;
  status: 'uploading' | 'processing' | 'completed' | 'error';
  progress: number;
  preview?: string;
}

type DocumentType = 'identity' | 'resume' | 'education' | 'experience' | 'other';

interface DocumentCategory {
  type: DocumentType;
  title: string;
  icon: React.ComponentType<{ className?: string }>;
  color: string;
  description: string;
  acceptedTypes: string[];
}

interface AstroBulkUploadProps {
  onUploadComplete: () => void;
  onShowTerms?: () => void;
  onBack: () => void;
}

export function AstroBulkUpload({ 
  onUploadComplete, 
  onShowTerms, 
  onBack 
}: AstroBulkUploadProps) {
  const [documents, setDocuments] = React.useState<Document[]>([]);
  const [isDragOver, setIsDragOver] = React.useState(false);
  const [isProcessing, setIsProcessing] = React.useState(false);
  const [showSuccess, setShowSuccess] = React.useState(false);
  const [overallProgress, setOverallProgress] = React.useState(0);

  const documentCategories: DocumentCategory[] = [
    {
      type: 'identity',
      title: 'Identity Documents',
      icon: User,
      color: 'bg-blue-100 text-blue-700 border-blue-200',
      description: 'Passport, Driver License, ID Card',
      acceptedTypes: ['image/*', 'application/pdf']
    },
    {
      type: 'resume',
      title: 'Resume & CV',
      icon: FileText,
      color: 'bg-green-100 text-green-700 border-green-200',
      description: 'Your professional resume',
      acceptedTypes: ['application/pdf', '.doc', '.docx']
    },
    {
      type: 'education',
      title: 'Education',
      icon: GraduationCap,
      color: 'bg-purple-100 text-purple-700 border-purple-200',
      description: 'Degrees, Certificates, Transcripts',
      acceptedTypes: ['application/pdf', 'image/*']
    },
    {
      type: 'experience',
      title: 'Experience',
      icon: Briefcase,
      color: 'bg-orange-100 text-orange-700 border-orange-200',
      description: 'Work certificates, References',
      acceptedTypes: ['application/pdf', 'image/*']
    }
  ];

  const fileInputRef = React.useRef<HTMLInputElement>(null);

  // Auto-categorize document based on filename and content
  const categorizeDocument = (file: File): DocumentType => {
    const fileName = file.name.toLowerCase();
    
    if (fileName.includes('resume') || fileName.includes('cv')) return 'resume';
    if (fileName.includes('degree') || fileName.includes('certificate') || fileName.includes('transcript')) return 'education';
    if (fileName.includes('experience') || fileName.includes('reference') || fileName.includes('letter')) return 'experience';
    if (fileName.includes('passport') || fileName.includes('license') || fileName.includes('id')) return 'identity';
    
    return 'other';
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    
    const files = Array.from(e.dataTransfer.files);
    processFiles(files);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    processFiles(files);
  };

  const processFiles = async (files: File[]) => {
    const newDocuments: Document[] = files.map(file => ({
      id: Math.random().toString(36).substr(2, 9),
      file,
      type: categorizeDocument(file),
      status: 'uploading',
      progress: 0,
      preview: file.type.startsWith('image/') ? URL.createObjectURL(file) : undefined
    }));

    setDocuments(prev => [...prev, ...newDocuments]);

    // Simulate upload and processing
    for (const doc of newDocuments) {
      await simulateUpload(doc.id);
    }
  };

  const simulateUpload = async (docId: string) => {
    // Upload simulation
    for (let progress = 0; progress <= 100; progress += 10) {
      await new Promise(resolve => setTimeout(resolve, 100));
      setDocuments(prev => prev.map(doc => 
        doc.id === docId ? { ...doc, progress } : doc
      ));
    }

    // Processing phase
    setDocuments(prev => prev.map(doc => 
      doc.id === docId ? { ...doc, status: 'processing' } : doc
    ));

    await new Promise(resolve => setTimeout(resolve, 2000));

    // Completed
    setDocuments(prev => prev.map(doc => 
      doc.id === docId ? { ...doc, status: 'completed', progress: 100 } : doc
    ));
  };

  const removeDocument = (docId: string) => {
    setDocuments(prev => prev.filter(doc => doc.id !== docId));
  };

  const retryUpload = (docId: string) => {
    setDocuments(prev => prev.map(doc => 
      doc.id === docId ? { ...doc, status: 'uploading', progress: 0 } : doc
    ));
    simulateUpload(docId);
  };

  const groupedDocuments = React.useMemo(() => {
    const grouped: Record<DocumentType, Document[]> = {
      identity: [],
      resume: [],
      education: [],
      experience: [],
      other: []
    };

    documents.forEach(doc => {
      grouped[doc.type].push(doc);
    });

    return grouped;
  }, [documents]);

  const completedCount = documents.filter(doc => doc.status === 'completed').length;
  const totalCount = documents.length;

  React.useEffect(() => {
    if (totalCount > 0) {
      setOverallProgress((completedCount / totalCount) * 100);
    }
  }, [completedCount, totalCount]);

  const handleContinue = () => {
    if (completedCount === totalCount && totalCount > 0) {
      setIsProcessing(true);
      setShowSuccess(true);
      setTimeout(() => {
        onUploadComplete();
      }, 3000);
    }
  };

  const allDocumentsProcessed = totalCount > 0 && completedCount === totalCount;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 flex flex-col relative overflow-hidden">
      {/* Cosmic Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <motion.div 
          className="absolute inset-0"
          animate={{ rotate: 360 }}
          transition={{ duration: 200, repeat: Infinity, ease: "linear" }}
        >
          {Array.from({ length: 30 }, (_, i) => (
            <div
              key={i}
              className="absolute w-1 h-1 bg-indigo-300/40 rounded-full"
              style={{
                top: `${Math.random() * 100}%`,
                left: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 10}s`
              }}
            />
          ))}
        </motion.div>

        {/* Floating elements */}
        <motion.div
          className="absolute top-32 left-16 w-24 h-24 bg-blue-200/20 rounded-full blur-2xl"
          animate={{ 
            y: [0, -20, 0],
            scale: [1, 1.1, 1]
          }}
          transition={{ duration: 6, repeat: Infinity }}
        />
        <motion.div
          className="absolute bottom-32 right-16 w-32 h-32 bg-indigo-200/20 rounded-full blur-2xl"
          animate={{ 
            y: [0, 20, 0],
            scale: [1.1, 1, 1.1]
          }}
          transition={{ duration: 8, repeat: Infinity }}
        />
      </div>

      {/* Header */}
      <motion.header
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative z-10 p-6 bg-white/70 backdrop-blur-sm border-b border-gray-200/50"
      >
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={onBack}
              className="text-gray-700 hover:text-gray-900 hover:bg-gray-100"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <div className="h-6 w-px bg-gray-300" />
            <img 
              src={openTurfLogo} 
              alt="OpenTurf Technologies" 
              className="h-8 w-auto"
            />
          </div>
          
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2 text-gray-700">
              <Rocket className="w-5 h-5 text-indigo-600" />
              <span>New Workflow - Step 2</span>
            </div>
          </div>
        </div>
      </motion.header>

      {/* Main Content */}
      <div className="flex-1 p-6">
        <div className="max-w-5xl mx-auto relative z-10">
          {!showSuccess ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-8"
            >
              {/* Header with Astro */}
              <div className="text-center">
                <motion.div 
                  className="mb-6 relative"
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.2 }}
                >
                  <AstroMascot expression="happy" size="lg" className="mx-auto" />
                  
                  {/* Floating Briefcase */}
                  <motion.div
                    className="absolute -right-4 top-0 w-16 h-16 bg-gradient-to-r from-indigo-400 to-purple-400 rounded-2xl flex items-center justify-center shadow-lg"
                    animate={{ 
                      y: [0, -8, 0],
                      rotate: [0, 5, -5, 0]
                    }}
                    transition={{ 
                      duration: 3,
                      repeat: Infinity,
                      ease: "easeInOut"
                    }}
                  >
                    <Files className="w-8 h-8 text-white" />
                  </motion.div>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 }}
                >
                  <h1 className="text-gray-900 mb-4 text-3xl">
                    Drop all your documents here 📄
                  </h1>
                  <p className="text-gray-600 text-lg max-w-2xl mx-auto leading-relaxed">
                    I'll sort them out while you cruise ahead. Just drag and drop everything at once!
                  </p>
                </motion.div>
              </div>

              {/* Upload Zone */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 }}
              >
                <Card 
                  className={`relative border-2 border-dashed transition-all duration-300 ${
                    isDragOver 
                      ? 'border-indigo-400 bg-indigo-50/50 scale-105' 
                      : 'border-gray-300 bg-white/80'
                  } backdrop-blur-sm p-8 text-center`}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    className="hidden"
                    onChange={handleFileSelect}
                    accept=".pdf,.doc,.docx,image/*"
                  />
                  
                  <motion.div
                    animate={{ 
                      scale: isDragOver ? 1.1 : 1,
                      rotate: isDragOver ? [0, 5, -5, 0] : 0
                    }}
                    className="mb-4"
                  >
                    <div className="w-16 h-16 bg-gradient-to-r from-indigo-500 to-purple-500 rounded-3xl mx-auto flex items-center justify-center mb-4">
                      <Upload className="w-8 h-8 text-white" />
                    </div>
                  </motion.div>

                  <h3 className="text-gray-900 mb-2 text-lg">
                    {isDragOver ? "Drop your documents here!" : "Drag & drop your documents"}
                  </h3>
                  
                  <p className="text-gray-600 mb-4 text-sm">
                    Or click to browse your files. We support PDF, Word documents, and images.
                  </p>

                  <Button
                    onClick={() => fileInputRef.current?.click()}
                    className="bg-gradient-to-r from-indigo-500 to-purple-500 hover:from-indigo-600 hover:to-purple-600 text-white px-6 py-2 mb-5"
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    Choose Files
                  </Button>

                  {/* Document Categories */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {documentCategories.map(category => (
                      <div key={category.type} className="text-center">
                        <div className={`w-10 h-10 rounded-xl mx-auto mb-2 flex items-center justify-center ${category.color}`}>
                          <category.icon className="w-5 h-5" />
                        </div>
                        <h4 className="text-gray-900 text-xs mb-1">{category.title}</h4>
                        <p className="text-gray-500 text-xs">{category.description}</p>
                      </div>
                    ))}
                  </div>

                  {/* Upload Progress Bar - Below Categories */}
                  <AnimatePresence>
                    {documents.length > 0 && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        className="mt-6 pt-4 border-t border-gray-200"
                      >
                        <div className="flex items-center justify-between mb-3">
                          <span className="text-sm text-gray-700">Upload Progress</span>
                          <span className="text-xs text-gray-600">
                            {completedCount} of {totalCount} documents processed
                          </span>
                        </div>
                        <Progress value={overallProgress} className="h-2 mb-3" />
                        {isProcessing && (
                          <motion.div
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="flex items-center justify-center space-x-2 text-indigo-600"
                          >
                            <RefreshCw className="w-3 h-3 animate-spin" />
                            <span className="text-xs">Fueling up… decoding your documents in the background</span>
                          </motion.div>
                        )}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </Card>
              </motion.div>

              {/* Document Review Section - Only show when all documents are processed */}
              <AnimatePresence>
                {allDocumentsProcessed && (

                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    className="space-y-6"
                  >
                    {/* Review Header */}
                    <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200 p-6">
                      <div className="flex items-start space-x-4">
                        <motion.div
                          animate={{ 
                            y: [0, -4, 0],
                            rotate: [0, 2, -2, 0]
                          }}
                          transition={{ 
                            duration: 3,
                            repeat: Infinity,
                            ease: "easeInOut"
                          }}
                        >
                          <AstroMascot expression="happy" size="md" />
                        </motion.div>
                        
                        <div className="flex-1">
                          <div className="bg-white rounded-2xl p-4 border border-blue-200 relative shadow-sm">
                            {/* Speech bubble pointer */}
                            <div className="absolute left-0 top-4 w-0 h-0 border-t-[6px] border-t-transparent border-b-[6px] border-b-transparent border-r-[10px] border-r-white -translate-x-2.5" />
                            
                            <h3 className="text-slate-900 mb-2">Perfect! Let's review your uploads 📋</h3>
                            <p className="text-slate-600 leading-relaxed">
                              I've organized all {totalCount} documents for you. Take a quick look to make sure everything's there, 
                              then we'll move forward with your onboarding journey!
                            </p>
                          </div>
                        </div>
                      </div>
                    </Card>

                    {/* Documents Ready for Review with Integrated Table */}
                    <Card className="bg-white/80 backdrop-blur-sm border border-white/20">
                      <div className="p-6 border-b border-gray-200">
                        <div className="flex items-center justify-between mb-4">
                          <div className="flex items-center space-x-3">
                            <div className="w-10 h-10 bg-gradient-to-r from-green-500 to-emerald-500 rounded-xl flex items-center justify-center">
                              <Check className="w-5 h-5 text-white" />
                            </div>
                            <div>
                              <h3 className="text-gray-900">Documents Ready for Review</h3>
                              <p className="text-gray-600 text-sm">All {totalCount} documents successfully processed</p>
                            </div>
                          </div>
                          <Badge variant="secondary" className="text-green-700 bg-green-100">
                            ✓ Complete
                          </Badge>
                        </div>

                        {/* Document Categories Summary */}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                          {documentCategories.map(category => {
                            const categoryDocs = groupedDocuments[category.type];
                            if (categoryDocs.length === 0) return null;

                            return (
                              <div key={category.type} className="text-center p-3 bg-gray-50 rounded-lg">
                                <div className={`w-8 h-8 rounded-lg mx-auto mb-2 flex items-center justify-center ${category.color}`}>
                                  <category.icon className="w-4 h-4" />
                                </div>
                                <h4 className="text-gray-900 text-sm mb-1">{category.title}</h4>
                                <p className="text-gray-600 text-xs">{categoryDocs.length} document{categoryDocs.length !== 1 ? 's' : ''}</p>
                              </div>
                            );
                          })}
                        </div>
                      </div>

                      {/* Integrated Document Table */}
                      <div className="max-h-96 overflow-y-auto">
                        <table className="w-full">
                          <thead className="bg-gray-50 sticky top-0">
                            <tr>
                              <th className="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">Filename</th>
                              <th className="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">Size</th>
                              <th className="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">Type</th>
                              <th className="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">Actions</th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {documents.map((doc, index) => {
                              // Get file type from MIME type or file extension
                              const getFileType = (file: File) => {
                                if (file.type.includes('pdf')) return 'PDF';
                                if (file.type.includes('word') || file.name.toLowerCase().includes('.doc')) return 'DOC';
                                if (file.type.includes('powerpoint') || file.name.toLowerCase().includes('.ppt')) return 'PPT';
                                if (file.type.includes('image/png')) return 'PNG';
                                if (file.type.includes('image/jpeg') || file.type.includes('image/jpg')) return 'JPG';
                                if (file.type.includes('image/gif')) return 'GIF';
                                if (file.type.includes('spreadsheet') || file.name.toLowerCase().includes('.xlsx')) return 'XLSX';
                                
                                // Fallback to file extension
                                const extension = file.name.split('.').pop()?.toUpperCase();
                                return extension || 'FILE';
                              };

                              const fileType = getFileType(doc.file);
                              const typeColor = {
                                'PDF': 'bg-red-100 text-red-800',
                                'DOC': 'bg-blue-100 text-blue-800',
                                'PPT': 'bg-orange-100 text-orange-800',
                                'PNG': 'bg-green-100 text-green-800',
                                'JPG': 'bg-green-100 text-green-800',
                                'GIF': 'bg-purple-100 text-purple-800',
                                'XLSX': 'bg-emerald-100 text-emerald-800'
                              }[fileType] || 'bg-gray-100 text-gray-800';

                              return (
                                <motion.tr
                                  key={doc.id}
                                  initial={{ opacity: 0, y: 10 }}
                                  animate={{ opacity: 1, y: 0 }}
                                  transition={{ delay: index * 0.05 }}
                                  className="hover:bg-gray-50 transition-colors"
                                >
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="flex items-center space-x-3">
                                      <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
                                        {doc.preview ? (
                                          <img 
                                            src={doc.preview} 
                                            alt="Document preview" 
                                            className="w-full h-full object-cover rounded-lg"
                                          />
                                        ) : doc.file.type.includes('pdf') ? (
                                          <FileText className="w-4 h-4 text-red-600" />
                                        ) : (
                                          <FileImage className="w-4 h-4 text-blue-600" />
                                        )}
                                      </div>
                                      <div className="flex-1 min-w-0">
                                        <p className="text-sm text-gray-900 truncate max-w-xs">{doc.file.name}</p>
                                        <div className="flex items-center space-x-1 mt-1">
                                          <Check className="w-3 h-3 text-green-600" />
                                          <span className="text-xs text-green-600">Processed</span>
                                        </div>
                                      </div>
                                    </div>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <span className="text-sm text-gray-900">
                                      {(doc.file.size / 1024 / 1024).toFixed(2)} MB
                                    </span>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <span className={`inline-flex px-2 py-1 text-xs rounded-full ${typeColor}`}>
                                      {fileType}
                                    </span>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={() => removeDocument(doc.id)}
                                      className="text-gray-400 hover:text-red-500"
                                    >
                                      <X className="w-4 h-4" />
                                    </Button>
                                  </td>
                                </motion.tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>

                      {/* Table Footer */}
                      <div className="px-6 py-3 border-t border-gray-200 bg-gray-50 text-sm text-gray-600">
                        {totalCount} files • {(documents.reduce((total, doc) => total + doc.file.size, 0) / 1024 / 1024).toFixed(1)} MB total
                      </div>
                    </Card>

                    {/* Continue Button */}
                    <div className="text-center pt-4">
                      <motion.div
                        initial={{ scale: 0.95 }}
                        animate={{ scale: 1 }}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        <Button
                          onClick={handleContinue}
                          className="bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white px-10 py-4 text-lg"
                        >
                          <div className="flex items-center space-x-3">
                            <Rocket className="w-5 h-5" />
                            <span>Continue Journey with {totalCount} Documents</span>
                            <ArrowLeft className="w-5 h-5 rotate-180" />
                          </div>
                        </Button>
                      </motion.div>
                      <p className="text-gray-500 text-sm mt-3">
                        All documents verified and ready for AI review
                      </p>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ) : (
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              className="text-center max-w-lg mx-auto"
            >
              {/* Success Astro */}
              <motion.div 
                className="mb-8"
                animate={{ 
                  y: [0, -10, 0],
                  rotate: [0, 5, -5, 0]
                }}
                transition={{ 
                  duration: 2,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
              >
                <AstroMascot expression="proud" size="xl" className="mx-auto" />
              </motion.div>

              <Card className="bg-gradient-to-r from-green-50 to-emerald-50 border-green-200 p-8">
                <div className="w-16 h-16 bg-gradient-to-r from-green-500 to-emerald-500 rounded-full mx-auto flex items-center justify-center mb-6">
                  <Sparkles className="w-8 h-8 text-white" />
                </div>
                
                <h2 className="text-gray-900 mb-4 text-2xl">
                  Documents Sorted! 🎉
                </h2>
                
                <p className="text-gray-600 mb-6 leading-relaxed">
                  All {totalCount} documents have been successfully uploaded and processed. 
                  Our AI has analyzed and categorized everything perfectly!
                </p>

                <motion.div
                  className="inline-flex items-center space-x-2 text-green-600"
                  animate={{ opacity: [1, 0.5, 1] }}
                  transition={{ duration: 1.5, repeat: Infinity }}
                >
                  <Sparkles className="w-5 h-5" />
                  <span className="text-sm">Launching next phase...</span>
                </motion.div>
              </Card>
            </motion.div>
          )}
        </div>
      </div>

      {/* Footer */}
      <motion.footer 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1 }}
        className="relative z-10 p-6 border-t border-gray-200/50 bg-white/50 backdrop-blur-sm"
      >
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <img 
              src={openTurfLogo} 
              alt="OpenTurf Technologies" 
              className="h-6 w-auto opacity-60"
            />
            <span className="text-xs text-gray-500">
              © {new Date().getFullYear()} OpenTurf Technologies Pvt. Ltd.
            </span>
          </div>
          
          <div className="flex items-center space-x-4 text-sm text-gray-600">
            <span>Powered by TurfAI</span>
            <span className="w-1 h-1 bg-gray-400 rounded-full"></span>
            <button 
              onClick={onShowTerms}
              className="text-purple-600 hover:text-purple-700 hover:underline transition-colors"
            >
              Terms & Conditions
            </button>
          </div>
        </div>
      </motion.footer>
    </div>
  );
}